// SA5 — Salud del sistema (cross-tenant).

import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SuperAdminSidebar } from '../../components/layout/SuperAdminSidebar';
import { useRefreshMvs, useSuperSalud } from '../../lib/api/queries';

function miles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}

function hace(min: number | null): string {
  if (min === null) return 'nunca';
  if (min < 60) return `hace ${min} min`;
  if (min < 60 * 24) return `hace ${Math.floor(min / 60)} h`;
  return `hace ${Math.floor(min / (60 * 24))} d`;
}

export function SA5SaludPage() {
  const { data, isLoading, isError } = useSuperSalud();
  const refreshMvs = useRefreshMvs();
  const [confirmando, setConfirmando] = useState(false);
  return (
    <RoleLayout sidebar={<SuperAdminSidebar />} roleLabel="Super Admin · Jidoka">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Salud del sistema</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              Métricas globales · todos los tenants
            </p>
          </div>
          {data && (
            <Badge tone={data.db_ok ? 'ok' : 'crit'} dot>
              {data.db_ok ? 'BD operativa' : 'BD con fallas'}
            </Badge>
          )}
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric
              label="Tenants"
              value={`${data.tenants_activos}/${data.tenants_total}`}
              sub="activos / total"
            />
            <Metric label="Usuarios" value={miles(data.usuarios_total)} sub="en el sistema" />
            <Metric label="Plantas" value={miles(data.plantas_total)} sub="totales" />
            <Metric label="Capturas 24h" value={miles(data.capturas_24h)} sub="producción" />
            <Metric label="Eventos 24h" value={miles(data.eventos_24h)} sub="auditoría" />
            <Metric
              label="Paros abiertos"
              value={miles(data.paros_abiertos)}
              sub="sin cerrar"
              tone={data.paros_abiertos > 0 ? 'warn' : 'ok'}
            />
          </div>
        )}
        {data && data.actividad_tenants.length > 0 ? (
          <Card>
            <CardContent className="flex flex-col gap-2 p-4">
              <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Actividad por tenant · ¿el piloto está vivo?
              </h2>
              <Table>
                <THead>
                  <TR>
                    <TH>Tenant</TH>
                    <TH className="text-right">Capturas (día op.)</TH>
                    <TH className="text-right">Última captura</TH>
                    <TH className="text-right">Paros abiertos</TH>
                    <TH className="text-right">Incidencias</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.actividad_tenants.map((t) => {
                    const muerto =
                      t.ultima_captura_hace_min === null || t.ultima_captura_hace_min > 60 * 24;
                    return (
                      <TR key={t.tenant_id}>
                        <TD className="font-medium">{t.tenant_nombre}</TD>
                        <TD className="text-right tabular-nums">{miles(t.capturas_hoy)}</TD>
                        <TD
                          className={`text-right tabular-nums ${muerto ? 'text-warn' : 'text-fg'}`}
                        >
                          {hace(t.ultima_captura_hace_min)}
                        </TD>
                        <TD className="text-right tabular-nums">{miles(t.paros_abiertos)}</TD>
                        <TD className="text-right tabular-nums">
                          {miles(t.incidencias_pendientes)}
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </CardContent>
          </Card>
        ) : null}
        {data && (
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-fg-tertiary">
              Generado: {new Date(data.generado_at).toLocaleString('es-CO')}
            </p>
            {/* Operación de emergencia tipada (spec SA §6) con confirmación doble */}
            {confirmando ? (
              <span className="flex items-center gap-2 text-xs">
                ¿Refrescar las vistas materializadas ahora?
                <Button
                  size="sm"
                  disabled={refreshMvs.isPending}
                  onClick={() => {
                    refreshMvs.mutate(undefined, {
                      onSuccess: (r) => {
                        toast.success(
                          `Vistas refrescadas en ${(r.duracion_ms / 1000).toFixed(1)}s`,
                        );
                        setConfirmando(false);
                      },
                      onError: (e) =>
                        toast.error('Falló el refresh', {
                          description: e instanceof Error ? e.message : undefined,
                        }),
                    });
                  }}
                >
                  {refreshMvs.isPending ? 'Refrescando…' : 'Sí, refrescar'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmando(false)}>
                  Cancelar
                </Button>
              </span>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirmando(true)}>
                Refrescar vistas materializadas
              </Button>
            )}
          </div>
        )}
      </div>
    </RoleLayout>
  );
}

function Metric({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'ok' | 'warn';
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          {label}
        </span>
        <span
          className={`text-2xl font-medium tabular-nums ${tone === 'warn' ? 'text-warn' : 'text-fg'}`}
        >
          {value}
        </span>
        <span className="text-[11px] text-fg-tertiary">{sub}</span>
      </CardContent>
    </Card>
  );
}
